import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import environment from '../config/environment';

// Types
interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  status?: number;
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor for auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for token refresh
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const original = error.config;
        
      if (error.response?.status === 401 && original && !(original as any)._retry) {
        (original as any)._retry = true;          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await axios.post(`${this.baseURL}/api/v1/auth/refresh`, {
                refresh_token: refreshToken
              });

              const { access_token } = response.data;
              localStorage.setItem('access_token', access_token);

              original.headers!.Authorization = `Bearer ${access_token}`;
              return this.api(original);
            }
          } catch (refreshError) {
            this.handleAuthError();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private handleAuthError() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }

  // Generic API methods
  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get(url, { params });
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post(url, data);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put(url, data);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.delete(url);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteWithData<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
     
      const response = await this.api.delete(url, { 
        data: data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
     
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error('❌ ApiService - DELETE error:', error);
      return this.handleError(error);
    }
  }

  async upload<T>(url: string, file: File, data?: any): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (data) {
        Object.keys(data).forEach(key => {
          formData.append(key, data[key]);
        });
      }

      const response = await this.api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  private handleError(error: any): ApiResponse {
    console.error('🔍 ApiService - Detailed error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });
    
    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data?.detail || 
                          error.response.data?.message || 
                          error.response.statusText ||
                          'An error occurred';
      
      return {
        error: errorMessage,
        status: error.response.status,
      };
    } else if (error.request) {
      // Request was made but no response received
      console.error('🔍 ApiService - No response received:', error.request);
      return {
        error: 'Network error - please check your connection and ensure the backend server is running',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        error: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  }

  // Download file method
  async downloadFile(url: string, filename?: string): Promise<void> {
    try {
      const response = await this.api.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get blob data
  async getBlob(url: string, params?: any): Promise<Blob> {
    try {
      console.log('🔍 ApiService - getBlob request:', { url, params });
      const response = await this.api.get(url, {
        params,
        responseType: 'blob',
      });
      console.log('✅ ApiService - getBlob response:', { 
        status: response.status,
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      return new Blob([response.data]);
    } catch (error) {
      console.error('❌ ApiService - getBlob error:', error);
      throw this.handleError(error);
    }
  }
}

// Create API service instance
const API_BASE_URL = environment.getApiBaseUrl();
export const apiService = new ApiService(API_BASE_URL);

export default ApiService;
